import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import 'bootstrap/dist/css/bootstrap.min.css';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import axiosClient, { UPLOADS_BASE_URL } from '../axios_client';

// Import images
import img1 from '../assets/AboutUs/WhatsApp Image 2025-08-14 at 14.28.12.jpeg';
import img2 from '../assets/AboutUs/WhatsApp Image 2025-08-14 at 14.28.32.jpeg';
import img3 from '../assets/AboutUs/s16.jpeg';
import img4 from '../assets/AboutUs/WhatsApp Image 2025-08-14 at 14.28.30.jpeg';
import img5 from '../assets/AboutUs/s15.jpeg';

const AboutUs = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [expandedEvents, setExpandedEvents] = useState({});

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000,
    arrows: false,
  };

  const carouselImages = [img1, img2, img3, img4, img5];

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axiosClient.get('/events');
        setEvents(response.data);
      } catch (err) {
        setError(err.response?.data?.error || t('aboutUs.eventsSection.error'));
      }
    };
    fetchEvents();
  }, [t]);

  // Format date to a readable format (e.g., "August 7, 2025")
  const formatDate = (dateString) => {
    if (!dateString) return t('aboutUs.eventsSection.noDate');
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Toggle expanded state for an event
  const toggleEvent = (eventId) => {
    setExpandedEvents((prev) => ({
      ...prev,
      [eventId]: !prev[eventId],
    }));
  };

  return (
    <section className="container my-5">
      {/* Introductory Section - Desktop (md and larger) */}
      <div className="row align-items-center hidden md:flex">
        {/* Left Content */}
        <div className="col-md-6 order-md-1 order-2">
          <h1 className="text-[#114131] text-2xl sm:text-3xl md:text-4xl font-bold mb-6 md:mb-10">
            {t('aboutUs.introSection.title')}
          </h1>
          <p className="text-gray-600 text-base sm:text-lg md:text-xl mb-6 md:mb-10">
            {t('aboutUs.introSection.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
            <button
              type="button"
              className="bg-[#E6A71A] border border-black rounded-md px-3 sm:px-4 py-2 text-white text-sm sm:text-base font-semibold hover:bg-[#d9990f] transition-colors duration-300"
              onClick={() => (window.location.href = '/ayurveda-spa')}
            >
              {t('aboutUs.introSection.learnMore')}
            </button>
            <button
              type="button"
              className="text-[#114131] border border-[#114131] rounded-md px-3 sm:px-4 py-1 sm:py-2 text-sm sm:text-base font-semibold hover:bg-[#114131] hover:text-white transition-colors duration-300"
              onClick={() => (window.location.href = '/')}
            >
              {t('aboutUs.introSection.seeVideo')}
            </button>
          </div>
        </div>

        {/* Right Image Section with Carousel */}
        <div className="col-md-6 order-md-2 order-1 text-center mb-4 md:mb-0">
          <Slider {...settings}>
            {carouselImages.map((image, idx) => (
              <div key={idx} className="position-relative">
                <img
                  src={image}
                  alt={t(`aboutUs.introSection.carouselImageAlt${idx + 1}`)}
                  className="img-fluid rounded-3xl w-full h-auto md:h-[400px] object-cover"
                />
              </div>
            ))}
          </Slider>
        </div>
      </div>

      {/* Introductory Section - Mobile (sm) */}
      <div className="flex flex-col items-center text-center md:hidden">
        <h1 className="text-[#114131] text-3xl sm:text-3xl font-bold mb-4 sm:mb-6">
          {t('aboutUs.introSection.title')}
        </h1>
        <div className="w-full max-w-[400px] mb-4 sm:mb-6">
          <Slider {...settings}>
            {carouselImages.map((image, idx) => (
              <div key={idx} className="position-relative">
                <img
                  src={image}
                  alt={t(`aboutUs.introSection.carouselImageAlt${idx + 1}`)}
                  className="img-fluid rounded-3xl w-full h-[250px] sm:h-[300px] object-cover"
                />
              </div>
            ))}
          </Slider>
        </div>
        <p className="text-gray-600 text-base sm:text-lg mb-4 sm:mb-6 max-w-[90%]">
          {t('aboutUs.introSection.description')}
        </p>
        <div className="flex flex-col gap-3 sm:gap-4 w-full max-w-[90%]">
          <button
            type="button"
            className="bg-[#E6A71A] border border-black rounded-md px-3 sm:px-4 py-2 text-white text-sm sm:text-base font-semibold hover:bg-[#d9990f] transition-colors duration-300"
            onClick={() => (window.location.href = '/ayurveda-spa')}
          >
            {t('aboutUs.introSection.learnMore')}
          </button>
          <button
            type="button"
            className="text-[#114131] border border-[#114131] rounded-md px-3 sm:px-4 py-1 sm:py-2 text-sm sm:text-base font-semibold hover:bg-[#114131] hover:text-white transition-colors duration-300"
            onClick={() => (window.location.href = '/')}
          >
            {t('aboutUs.introSection.seeVideo')}
          </button>
        </div>
      </div>

      {/* Our Events Section - Desktop (md and larger) */}
      <div className="hidden md:block mt-6 sm:mt-8 md:mt-10">
        <h2 className="text-[#114131] text-2xl sm:text-3xl md:text-[3.5rem] font-bold mb-4 sm:mb-6 md:mb-8 text-center">
          {t('aboutUs.eventsSection.title')}
        </h2>
        {error && <p className="text-red-500 mb-4 sm:mb-6 text-center text-sm sm:text-base">{error}</p>}
        {events.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-bordered table-hover w-full">
              <thead className="thead-dark bg-gray-800 text-white">
                <tr>
                  <th scope="col" className="px-4 py-2">{t('aboutUs.eventsSection.table.eventName')}</th>
                  <th scope="col" className="px-4 py-2">{t('aboutUs.eventsSection.table.date')}</th>
                  <th scope="col" className="px-4 py-2">{t('aboutUs.eventsSection.table.description')}</th>
                  <th scope="col" className="px-4 py-2">{t('aboutUs.eventsSection.table.images')}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="px-4 py-2">{event.event_name}</td>
                    <td className="px-4 py-2">{formatDate(event.created_at)}</td>
                    <td className="px-4 py-2">{event.description || t('aboutUs.eventsSection.noDescription')}</td>
                    <td className="px-4 py-2">
                      {event.images && event.images.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {event.images.map((img, index) => (
                            <img
                              key={index}
                              src={`${UPLOADS_BASE_URL}${img}`}
                              alt={t('aboutUs.eventsSection.eventImageAlt', { eventName: event.event_name, index: index + 1 })}
                              className="rounded w-auto h-[150px] object-cover"
                              onError={(e) => console.error(`Failed to load image: ${UPLOADS_BASE_URL}${img}`)}
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-600">{t('aboutUs.eventsSection.noImages')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-600 text-center text-sm sm:text-base">{t('aboutUs.eventsSection.noEvents')}</p>
        )}
      </div>

      {/* Our Events Section - Mobile (sm) */}
      <div className="block md:hidden mt-6 sm:mt-8">
        <h2 className="text-[#114131] text-3xl sm:text-3xl font-bold mb-4 sm:mb-6 text-center">
          {t('aboutUs.eventsSection.title')}
        </h2>
        {error && <p className="text-red-500 mb-4 sm:mb-6 text-center text-sm sm:text-base">{error}</p>}
        {events.length > 0 ? (
          <div className="flex flex-col gap-4">
            {events.map((event) => (
              <div key={event.id} className="border border-gray-300 rounded-lg p-3 sm:p-4">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => toggleEvent(event.id)}
                >
                  <h3 className="text-gray-800 text-base sm:text-lg font-semibold">
                    {event.event_name}
                  </h3>
                  <svg
                    className="w-5 h-5 sm:w-6 sm:h-6 text-[#114131]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    {expandedEvents[event.id] ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M20 12H4"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 4v16m8-8H4"
                      />
                    )}
                  </svg>
                </div>
                {expandedEvents[event.id] && (
                  <div className="mt-3 sm:mt-4 flex flex-col gap-3 sm:gap-4 text-center">
                    <div>
                      <h4 className="text-gray-700 text-sm sm:text-base font-medium">
                        {t('aboutUs.eventsSection.table.eventName')}
                      </h4>
                      <p className="text-gray-600 text-sm sm:text-base">{event.event_name}</p>
                    </div>
                    <div>
                      <h4 className="text-gray-700 text-sm sm:text-base font-medium">
                        {t('aboutUs.eventsSection.table.date')}
                      </h4>
                      <p className="text-gray-600 text-sm sm:text-base">{formatDate(event.created_at)}</p>
                    </div>
                    <div>
                      <h4 className="text-gray-700 text-sm sm:text-base font-medium">
                        {t('aboutUs.eventsSection.table.description')}
                      </h4>
                      <p className="text-gray-600 text-sm sm:text-base">
                        {event.description || t('aboutUs.eventsSection.noDescription')}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-gray-700 text-sm sm:text-base font-medium">
                        {t('aboutUs.eventsSection.table.images')}
                      </h4>
                      {event.images && event.images.length > 0 ? (
                        <div className="flex flex-col gap-2 sm:gap-3 items-center">
                          {event.images.map((img, index) => (
                            <img
                              key={index}
                              src={`${UPLOADS_BASE_URL}${img}`}
                              alt={t('aboutUs.eventsSection.eventImageAlt', { eventName: event.event_name, index: index + 1 })}
                              className="rounded w-full max-w-[300px] h-[200px] sm:h-[250px] object-cover"
                              onError={(e) => console.error(`Failed to load image: ${UPLOADS_BASE_URL}${img}`)}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-600 text-sm sm:text-base">{t('aboutUs.eventsSection.noImages')}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600 text-center text-sm sm:text-base">{t('aboutUs.eventsSection.noEvents')}</p>
        )}
      </div>
    </section>
  );
};

export default AboutUs;